import { ROUTES } from "./routes_new";

/**
  All tracking events should reside in this file. Group in different objects if it makes sense!

  ================= Description on how to format events from mixpanel docs: =================

  Keep Events Generic

  # We recommend keeping event names generic and using properties for all context. For example:

  Instead of tracking events called Home Page Viewed and Pricing Page Viewed, track a Page Viewed event
  with a Page Name property set to /home or /pricing. Instead of tracking Blue Button Clicked or Checkout
  Button Clicked, track Button Clicked with a Color property set to Blue and Button Name set to Checkout.

  # Name Events and Properties Consistently

  We recommend having a consistent naming convention for your events and properties. For example:

  > Use camel case for your event names.
  > Use the (Object) (Verb) format for event names. Like "Song Played" or "Page Viewed".

  # Avoid Creating Event or Property Names Dynamically

  For example, don't create an event name like Purchase (11-01-2019). Instead,
  create an event called Purchase and have some property (eg: Return Date) set to the dynamic value 11-01-2019).

*/
export const EVENT = {
  SHARE_ALERT: "Alert Share Dialog Open",
  EXPORT_ALERT: "Alert Export Dialog Open",
  SHARE_ALERT_COPIED: "Alert Mentions Feed Share Link Copied",
  SHARE_ALERT_CLICKED: "Alert Mentions Feed Share Link Clicked",
  SHARE_ALERT_CLICKED_RSS: "Alert Mentions RSS Clicked",
  SHARE_ALERT_CLICKED_API: "Alert Mentions API Clicked",
  SHARE_ALERT_CLICKED_CSV: "Alert Mentions CSV Clicked",
  SHARE_ALERT_CLICKED_EXCEL: "Alert Mentions Excel Clicked",
  SHARE_MENTION: "Mention Share Dialog Open",
  SHARE_MENTION_COPIED: "Mention Share Link Copied",
  SHARE_MENTION_CLICKED: "Mention Share Link Clicked",
  SHARE_REPORT: "Report Share Link",
  SHARE_TAG: "Tag Share Dialog Open",
  EXPORT_TAG: "Tag Export Dialog Open",
  SHARE_TAG_COPIED: "Tag Share Link Copied",
  SHARE_TAG_CLICKED: "Tag Share Link Clicked",
  SHARE_TAG_CLICKED_RSS: "Alert Mentions RSS Clicked",
  SHARE_TAG_CLICKED_CSV: "Alert Mentions CSV Clicked",
  SHARE_TAG_CLICKED_EXCEL: "Alert Mentions Excel Clicked",
  SHARE_ALERT_ANALYTICS_CLICKED: "Alert Analytics Share Link Clicked",
  SHARE_ALERT_ANALYTICS_COPIED: "Alert Analytics Share Link Copied",
  EXTERNAL_URL_CLICK: "Mention Source Clicked",
  DELETE_ALERT: "Alert Deleted",
  MARK_FAVOURITE: "Mention Favourited",
  MENTION_SORTING: "Mentions Ordered",
  PLAYED_MENTION: "Mention Played",
  TAGGED_MENTION: "Mention Tagged",
  INVITE_USER: "User Invited",
  EDIT_USER: "User Edited",
  ADD_REPORT_SUBSCRIPTION: "User Added Report Subscription",
  REMOVE_REPORT_SUBSCRIPTION: "User Removed Report Subscription",
  REMOVE_USER: "User Removed",
  UPDATE_SETTINGS: "Settings Updated",
  GENERATE_PPT: "PowerPoint Generated",
  DOWNLOAD_PPT: "PowerPoint Downloaded",
  TOGGLE_ALERT_ANALYTICS: "Alert-Analytics Toggled",
  WIDGET_CLICK: "Widget Clicked",
  INSIGHT_WIDGET_CLICK: "Insight Widget Clicked",
  DATE_PRESET_CHANGE: "Date Period Changed",
  PAGE_VIEW: "Page Viewed",
  ALERT_LIMIT_REACHED_VIEWED: "Alert Limit Reached Viewed",
  MENTION_TRANSLATED: "Mention Translated",
  CREATED_REPORT: "Created Report",
  CREATED_ALERT: "Created Alert",
  DELETED_ALERT: "Alert Deleted",
  EDITED_ALERT: "Alert Edited",
  SAVE_MENTIONS: "Save Mentions",
  EXPORT_MENTIONS_TO_EXCEL: "Export Mentions to excel",
  EXPORT_MENTIONS_TO_CSV: "Export Mentions to csv",
  SHARED_MENTION: "Shared mention",
  MARKED_MENTION_RELEVANT: "Marked mention relevant",
  MARKED_MENTION_IRRELEVANT: "Marked mention irrelevant",
  ADDED_MENTION_TAG: "Added mention tag",
  REMOVED_MENTION_TAG: "Removed mention tag",
  USER_ACTIVATED: "User Activated",
  INVITED_COLLEAGUE: "Invited colleague",
  SETTINGS_UPDATED: "Settings Updated",
  USER_DELETED: "User Deleted",
  CREATED_REPORT_FROM_TEMPLATE: "Created Report from Template",
  PDF_GENERATED: "Generated pdf-report",
  EDIT_REPORT: "Edit report",

  ONBOARDING_SUCCESSFUL_URL_SUBMISSION:
    "Onboarding - URL submitted successfully",
  ONBOARDING_UNSUCCESSFUL_URL_SUBMISSION: "Onboarding - Failed to submit URL",
  ONBOARDING_USE_CASES_SELECTED: "Onboarding - Use cases selected",

  SEARCH_SETTINGS_OPENED: "Search - Settings opened",
  SEARCH_SETTINGS_CLOSED: "Search - Settings closed",
  SEARCH_QUERY_ADDED: "Search - Query added",
  SEARCH_QUERY_DELETED: "Search - Query deleted",
  SEARCH_QUERY_DUPLICATED: "Search - Query duplicated",
  SEARCH_QUERY_ADVANCED_OPENED: "Search - Query - Advanced opend",
  SEARCH_QUERY_ADVANCED_CLOSED: "Search - Query - Advanced closed",

  MENTIONS_FILTERED: "Mentions filtered",
  MENTIONS_FILTER_CLEARED: "Mentions Filter Cleared",
} as const;

export const PAGE_EVENT = {
  // Active alerts
  Alert: ROUTES.MENTION_LIST_ORGID,
  "Alert Mentions Feed": ROUTES.MENTION_DETAIL_ORGID,
  "Alert Mentions Sheet": ROUTES.MENTION_DETAIL_ORGID_PERIOD,
  "Alert Insights Results": ROUTES.MENTION_INSIGHTS_RESULTS_ORGID,
  "Alert Insights Results Sheet": ROUTES.MENTION_INSIGHTS_RESULTS_PERIOD_ORGID,
  "Alert Insights Reach": ROUTES.MENTION_INSIGHTS_REACH_ORGID,
  "Alert Insights Reach Sheet": ROUTES.MENTION_INSIGHTS_REACH_PERIOD_ORGID,
  "Alert Insights Sentiment": ROUTES.MENTION_INSIGHTS_SENTIMENT_ORGID,
  "Alert Insights Sentiment Sheet":
    ROUTES.MENTION_INSIGHTS_SENTIMENT_PERIOD_ORGID,
  "Alert Settings": ROUTES.MENTION_SETTINGS_ORGID,
  // Archived alerts
  Archive: ROUTES.ARCHIVE_ORGID,
  "Archive Detail": ROUTES.ARCHIVE_DETAIL_ORGID,
  "Archive Settings": ROUTES.ARCHIVE_SETTINGS_ORGID,
  // Saved searches
  "Saved Searches": ROUTES.SAVED_SEARCHES_ORGID,
  "Saved Searches Detail": ROUTES.SAVED_SEARCHES_DETAIL_ORGID,
  "Saved Searches Settings": ROUTES.SAVED_SEARCHES_SETTINGS_ORGID,
  // Other pages
  Favorites: ROUTES.FAVORITES_ORGID,
  Tag: ROUTES.TAGS_ORGID,
  "Report List": ROUTES.REPORTS_ORGID,
  "Report Edit": ROUTES.REPORTS_EDIT_ORGID,
  "User Invite": ROUTES.INVITE_USER_ORGID,
  Settings: ROUTES.SETTINGS_ORGID,
  "Settings Email": ROUTES.SETTINGS_EMAIL_ORGID,
  "Settings Team": ROUTES.SETTINGS_TEAM_ORGID,
  "Settings API": ROUTES.SETTINGS_API_ORGID,
  Search: ROUTES.SEARCH_ORGID,
  SearchPreview: ROUTES.SEARCH_PREVIEW_ORGID,
  SearchSubmit: ROUTES.SEARCH_SUBMIT_ORGID,
  // Pages without org-scoped equivalents
  Activation: ROUTES.ACTIVATION,
  Signup: ROUTES.SIGNUP_LOCALIZED,
  SignupInfo: ROUTES.SIGNUP_INFO_LOCALIZED,
  SignupConfirm: ROUTES.SIGNUP_CONFIRM_LOCALIZED,
  Welcome: ROUTES.WELCOME,
} as const;
